import React, { useState } from 'react';
import { useAppStateContext } from '../hooks/useAppState';

const API_BASE = process.env.API_BASE || "https://smcbmp.api.sccwrp.org";

export default function DataDownloadTab() {
  const { state, bmpState, getBmpTypeDisplayName } = useAppStateContext();
  const hasMPIData = state.mpiData && state.mpiData.length > 0;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Build the request body for the download endpoint
  function buildRequestBody() {
    const { analysistype, bmptype, sitename, bmpname, selectedBmpType } = bmpState;
    let body = {};
    if (analysistype === "treatmenttrain") {
      body = { sitename };
    } else if (analysistype === "bmptype") {
      body = { bmptype,  bmptypename: getBmpTypeDisplayName(bmptype) };
    } else if (analysistype === "individual") {
      body = { sitename, bmpname };
      if (selectedBmpType) {
        body.selected_bmp_type_code = selectedBmpType;
        // convert BMP type code to name
        body.selected_bmp_type_name = getBmpTypeDisplayName(selectedBmpType); 
      }
    }
    // Always include analytes array, using MPI data
    body.analytes = state.mpiData.map(a => {
      // For hydrology, threshold is not needed
      if (a.analytename === "Hydrology") {
        return { analytename: "Hydrology" };
      }
      return { analytename: a.analytename, threshold: a.threshold };
    });
    return body;
  }

  async function handleDownload() {
    setLoading(true);
    setError(null);
    try {
      const { analysistype, bmptype, sitename, bmpname, selectedBmpType } = bmpState;
      const url = `${API_BASE}/download/${analysistype}`;
      const body = buildRequestBody();
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let errMsg = `HTTP ${res.status}`;
        try {
          const errData = await res.json();
          errMsg = errData.error || errData.message || errMsg;
        } catch {}
        throw new Error(errMsg);
      }

      
      // Build fallback filename
      let fallbackFilename = 'performance_data'; // fallback
      let filename = fallbackFilename;
      
      switch (analysistype) {
        case 'individual':
          const selectedBmpTypeName = selectedBmpType ? getBmpTypeDisplayName(selectedBmpType) : '';
          filename = `${sitename || ''}-${bmpname || ''}-${selectedBmpTypeName}_data`;
          break;
        case 'bmptype':
          const bmptypename = getBmpTypeDisplayName(bmptype);
          filename = `${bmptypename || ''}_data`;
          break;
        case 'treatmenttrain':
          filename = `${sitename || ''}_data`;
          break;
        default:
          break;
      }
      
      // Clean up any double dashes or trailing dashes
      filename = filename.replace(/-+/g, '-').replace(/^-|-$/g, '');
      filename = `${filename}.xlsx`;

      // The response is a base64-encoded Excel file
      const blob = await res.blob();
      // Create a download link
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlObj);
    } catch (e) {
      setError(e.message || "Failed to download file.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-xl mx-auto p-6 bg-white rounded shadow">
      <h2 className="text-xl font-bold mb-2">Data Download</h2>
      <p className="mb-4 text-gray-700">
        Export the data used for current analysis selections. 
        You must press “Go” on each tab (water quality and/or hydrology) for the data to be available for download. 
      </p>
      <button
        className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
        disabled={!hasMPIData || loading}
        onClick={handleDownload}
      >
        {loading ? "Downloading..." : "Download Data"}
      </button>
      {!hasMPIData && (
        <div className="mt-2 text-sm text-gray-500">Add analytes to the MPI selection to enable data download.</div>
      )}
      {error && (
        <div className="mt-2 text-sm text-red-600">{error}</div>
      )}
    </div>
  );
} 